/**
 * Date and timezone utility functions
 */

/**
 * Convert a UTC timestamp string to Eastern Time and format it
 * @param dateString - ISO 8601 UTC timestamp string
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date string in Eastern Time
 * @throws Error if dateString is invalid
 */
export function formatDateTimeEastern(
  dateString: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.error(`Invalid date string: ${dateString}`);
    return 'Invalid date';
  }
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
    ...options,
  };
  
  return date.toLocaleString('en-US', defaultOptions);
}

/**
 * Format a date in Eastern Time (date only, no time)
 * @param dateString - ISO 8601 UTC timestamp string
 * @returns Formatted date string like "Jan 13, 2024"
 */
export function formatDateEastern(dateString: string): string {
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.error(`Invalid date string: ${dateString}`);
    return 'Invalid date';
  }
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/New_York',
  });
}

/**
 * Format a short date for charts (month and day only)
 * @param dateString - ISO 8601 UTC timestamp string
 * @returns Formatted date string like "Jan 13"
 */
export function formatShortDateEastern(dateString: string): string {
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.error(`Invalid date string: ${dateString}`);
    return 'Invalid';
  }
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}
